/**
 * wayai eval call — place scored voice calls for an eval journey (the call-eval runner,
 * gh #4418 plan §12.3). See `printEvalCallHelp` for usage, and `lib/call-eval/` for how a run
 * goes: the runner is a machine caller that speaks the journey's lines over WebRTC, straight to
 * the provider's voice layer, through the eval call route; WayAI's backend never carries the
 * call's audio.
 */

import { accessSync, constants as fsConstants, existsSync, readFileSync, statSync, writeFileSync } from 'node:fs';
import { dirname, resolve } from 'node:path';
import { EVAL_CALL_MAX_SECONDS, EVAL_CALL_MIN_SECONDS } from '@wayai/core/contracts';
import type { JourneyResponse } from '@wayai/core/contracts';
import { requireAuth, getAccessToken } from '../lib/auth.js';
import { requireRepoConfig } from '../lib/repo-config.js';
import { resolveActiveHubId } from '../lib/workspace.js';
import { ApiClient } from '../lib/api-client.js';
import { wantsHelp } from '../lib/utils.js';
import { extractApiMessage } from '../lib/errors.js';
import { HANDLED_SIGNALS, SIGNAL_EXIT_CODE, type HandledSignal } from '../lib/eval-session-control.js';
import { ClipDirectorySource, OpenAiTtsSource, DEFAULT_TTS_MODEL, DEFAULT_TTS_VOICE, type CallerAudioSource } from '../lib/call-eval/caller-audio.js';
import { CallMedia, WRTC_INSTALL, loadWrtcRuntime } from '../lib/call-eval/call-media.js';
import { buildCallScript, parseCallPlan, type CallPlan } from '../lib/call-eval/script.js';
import { runCallEvalSuite, systemRunnerClock } from '../lib/call-eval/runner.js';
import { formatSuiteReport } from '../lib/call-eval/report.js';
import { DEFAULT_THRESHOLDS, type ScoreThresholds } from '../lib/call-eval/score.js';
import { DEFAULT_COST_CAP, type CostCap } from '../lib/call-eval/cost-cap.js';

const DEFAULT_RUNS = 1;

/** Most runs one invocation places. */
const MAX_RUNS = 50;

export interface EvalCallArgs {
  journey: string;
  runs: number;
  planPath: string | null;
  clipsDir: string | null;
  ttsVoice: string;
  ttsModel: string;
  cap: CostCap;
  thresholds: ScoreThresholds;
  jsonPath: string | null;
}

function fail(message: string): never {
  throw new Error(message);
}

function numberFlag(name: string, raw: string | undefined, { min, max, integer }: { min: number; max: number; integer: boolean }): number {
  if (raw === undefined || raw.startsWith('--')) fail(`${name} requires a value.`);
  const n = Number(raw);
  if (!Number.isFinite(n) || (integer && !Number.isInteger(n)) || n < min || n > max) {
    fail(`${name} must be ${integer ? 'a whole number' : 'a number'} from ${min} to ${max}.`);
  }
  return n;
}

function stringFlag(name: string, raw: string | undefined): string {
  if (!raw || raw.startsWith('--')) fail(`${name} requires a value.`);
  return raw;
}

/** Parse the command's flags (`--hub` is taken out before). Throws an `Error` for a usage mistake. */
export function parseEvalCallArgs(args: string[]): EvalCallArgs {
  const parsed: EvalCallArgs = {
    journey: '',
    runs: DEFAULT_RUNS,
    planPath: null,
    clipsDir: null,
    ttsVoice: DEFAULT_TTS_VOICE,
    ttsModel: DEFAULT_TTS_MODEL,
    cap: { ...DEFAULT_COST_CAP },
    thresholds: { ...DEFAULT_THRESHOLDS },
    jsonPath: null,
  };
  for (let i = 0; i < args.length; i++) {
    const arg = args[i];
    switch (arg) {
      case '--journey': parsed.journey = stringFlag(arg, args[++i]); break;
      case '--runs': parsed.runs = numberFlag(arg, args[++i], { min: 1, max: MAX_RUNS, integer: true }); break;
      case '--plan': parsed.planPath = stringFlag(arg, args[++i]); break;
      case '--clips': parsed.clipsDir = stringFlag(arg, args[++i]); break;
      case '--tts-voice': parsed.ttsVoice = stringFlag(arg, args[++i]); break;
      case '--tts-model': parsed.ttsModel = stringFlag(arg, args[++i]); break;
      case '--max-call-seconds':
        parsed.cap.maxCallSeconds = numberFlag(arg, args[++i], { min: EVAL_CALL_MIN_SECONDS, max: EVAL_CALL_MAX_SECONDS, integer: true });
        break;
      case '--max-operations': parsed.cap.maxOperations = numberFlag(arg, args[++i], { min: 1, max: 100_000, integer: true }); break;
      case '--usd-per-minute': parsed.cap.providerUsdPerMinute = numberFlag(arg, args[++i], { min: 0, max: 100, integer: false }); break;
      case '--max-usd': parsed.cap.maxSuiteUsd = numberFlag(arg, args[++i], { min: 0, max: 10_000, integer: false }); break;
      case '--latency-p95-ms':
        parsed.thresholds.latencyP95MaxMs = numberFlag(arg, args[++i], { min: 100, max: 120_000, integer: true });
        break;
      case '--json': parsed.jsonPath = stringFlag(arg, args[++i]); break;
      default: fail(`Unknown flag for \`eval call\`: ${arg}`);
    }
  }
  if (!parsed.journey) fail('Name the journey to call: --journey <name or id>.');
  if (parsed.jsonPath) checkReportPath(parsed.jsonPath);
  return parsed;
}

/**
 * Checked before any call is placed: a report that cannot be written would be lost after the
 * spend. Catches a missing or read-only folder and a path that is a folder; a write can still
 * fail later (a full disk), and then the command exits 1 (`suiteExitCode`).
 */
function checkReportPath(path: string): void {
  const target = resolve(path);
  const dir = dirname(target);
  if (!existsSync(dir) || !statSync(dir).isDirectory()) fail(`--json ${path}: the folder ${dir} does not exist.`);
  if (existsSync(target) && statSync(target).isDirectory()) fail(`--json ${path} is a folder; name a file.`);
  try {
    accessSync(existsSync(target) ? target : dir, fsConstants.W_OK);
  } catch {
    fail(`--json ${path} cannot be written here.`);
  }
}

/**
 * As run-eval: 0 only when every run the suite was asked for passed, and the report it was asked
 * to write was written, so a CI job cannot go green on a partial suite or a lost report.
 */
export function suiteExitCode(report: { pass_rate: { passed: number } }, runs: number, reportWritten: boolean): 0 | 1 {
  return report.pass_rate.passed === runs && reportWritten ? 0 : 1;
}

/** Pre-recorded clips when asked for, else the operator's own OpenAI key; neither is an error. */
export function chooseAudioSource(options: EvalCallArgs, plan: CallPlan, env: NodeJS.ProcessEnv = process.env): CallerAudioSource {
  const planDir = options.planPath ? dirname(resolve(options.planPath)) : process.cwd();
  const planHasClips = Object.values(plan.lines).some((line) => line.clip);
  if (options.clipsDir || planHasClips) {
    if (options.clipsDir && !existsSync(options.clipsDir)) fail(`--clips ${options.clipsDir} does not exist.`);
    return new ClipDirectorySource(options.clipsDir ? resolve(options.clipsDir) : null, planDir);
  }
  const apiKey = env.OPENAI_API_KEY;
  if (!apiKey) {
    fail('The caller needs audio: give pre-recorded clips (--clips <dir>), or set OPENAI_API_KEY to synthesize them with your own OpenAI key.');
  }
  return new OpenAiTtsSource({ apiKey, voice: options.ttsVoice, model: options.ttsModel });
}

export interface InterruptControl {
  /** True once a signal arrived: the run in progress winds down, and no further run starts. */
  interrupted(): boolean;
  /** The first signal's conventional exit code, or null when none arrived. */
  exitCode(): number | null;
  dispose(): void;
}

/**
 * A call placed by the runner lives on the provider and in the hub, not in this process: exiting
 * on Ctrl-C would leave it spending provider minutes and call-meter operations until its own
 * limits, and its eval conversation and synthetic caller open. So the first SIGINT, SIGTERM or
 * SIGHUP (a closed terminal) only asks the runner to stop — it hangs up, reads the record and
 * finishes the call as for any stopped run — and a second one leaves at once, saying what it
 * leaves behind.
 */
export function installInterruptHandlers(exit: (code: number) => void = (code) => process.exit(code)): InterruptControl {
  let first: HandledSignal | null = null;
  const listeners = new Map<HandledSignal, () => void>();
  const dispose = () => {
    for (const [signal, listener] of listeners) process.off(signal, listener);
    listeners.clear();
  };
  const handle = (signal: HandledSignal) => {
    if (first) {
      console.error('\nSecond signal: leaving now. A call in progress ends on its own limits (at most its --max-call-seconds), and its eval conversation stays open.');
      dispose();
      exit(SIGNAL_EXIT_CODE[first]);
      return;
    }
    first = signal;
    console.error(`\n${signal} received: hanging up the call in progress and closing its eval conversation; no further run starts. Send it again to leave now.`);
  };
  for (const signal of HANDLED_SIGNALS) {
    const listener = () => handle(signal);
    listeners.set(signal, listener);
    process.on(signal, listener);
  }
  return {
    interrupted: () => first !== null,
    exitCode: () => (first ? SIGNAL_EXIT_CODE[first] : null),
    dispose,
  };
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** The journey by id (its own route) or by name (the hub's list). */
async function findJourney(client: ApiClient, hubId: string, selector: string): Promise<JourneyResponse | null> {
  if (UUID_RE.test(selector)) {
    try {
      return (await client.getJourney(hubId, selector)).data.journey;
    } catch {
      // Not a journey id on this hub: it may still be a journey's name.
    }
  }
  return (await client.listJourneys(hubId)).data.journeys.find((j) => j.journey_name === selector) ?? null;
}

export async function evalCallCommand(argv: string[]): Promise<void> {
  if (wantsHelp(argv)) {
    printEvalCallHelp();
    return;
  }
  const args = [...argv];
  const hubId = resolveActiveHubId(args);
  const { config, accessToken } = await requireAuth();
  requireRepoConfig();

  let options: EvalCallArgs;
  let plan: CallPlan;
  try {
    options = parseEvalCallArgs(args);
    plan = options.planPath ? parseCallPlan(JSON.parse(readFileSync(options.planPath, 'utf8'))) : { lines: {} };
  } catch (err) {
    console.error(extractApiMessage(err));
    process.exit(1);
  }

  const client = new ApiClient({ apiUrl: config.api_url, accessToken, onUnauthorized: () => getAccessToken(config) });
  const journey = await findJourney(client, hubId, options.journey);
  if (!journey) {
    console.error(`No journey "${options.journey}" on this hub.`);
    process.exit(1);
  }

  let audio: CallerAudioSource;
  let script;
  try {
    script = buildCallScript(journey, plan);
    audio = chooseAudioSource(options, plan);
  } catch (err) {
    console.error(extractApiMessage(err));
    process.exit(1);
  }

  // Loaded before any call is placed: a missing WebRTC stack fails here, spending nothing.
  const runtime = await loadWrtcRuntime();
  console.log(`Journey "${journey.journey_name}": ${script.lines.length} caller line(s), ${options.runs} run(s).`);
  console.log(`Cap per run: ${options.cap.maxCallSeconds} s of call and ${options.cap.maxOperations} WayAI operations; suite budget $${options.cap.maxSuiteUsd.toFixed(2)} of provider time.`);

  const interrupts = installInterruptHandlers();
  let report;
  try {
    report = await runCallEvalSuite({
      api: client,
      hubId,
      script,
      audio,
      cap: options.cap,
      thresholds: options.thresholds,
      // One clock for the runner and its media: every time they compare is on it.
      clock: systemRunnerClock,
      createMedia: async () => new CallMedia(runtime, systemRunnerClock),
      runs: options.runs,
      log: (line) => console.log(line),
      interrupted: () => interrupts.interrupted(),
    });
  } catch (err) {
    // The caller's audio could not be prepared: no call was placed.
    console.error(extractApiMessage(err));
    process.exit(1);
  } finally {
    interrupts.dispose();
  }

  console.log(`\n${formatSuiteReport(report)}`);
  let reportWritten = true;
  if (options.jsonPath) {
    try {
      writeFileSync(options.jsonPath, `${JSON.stringify(report, null, 2)}\n`);
      console.log(`Report written to ${options.jsonPath}`);
    } catch (err) {
      reportWritten = false;
      console.error(`The report could not be written to ${options.jsonPath}: ${extractApiMessage(err)}`);
    }
  }
  const signalCode = interrupts.exitCode();
  if (signalCode !== null) process.exit(signalCode);
  if (suiteExitCode(report, options.runs, reportWritten) !== 0) process.exit(1);
}

export function printEvalCallHelp(): void {
  console.log(`
wayai eval call — place scored voice calls for an eval journey

Usage:
  wayai eval call --journey <name|id> [options]

A machine caller speaks the journey's caller lines in real time on a real call to the
journey's voice agent (a pilot_voice responder), over WebRTC straight to the provider.
Each run is scored on: recognition of numbers and CPFs, delegation recall, the answer's
facts, unvetted claims (every figure the voice says against the delegation results),
read-back, style and latency. The judged half is \`wayai run-eval --call-mode\`.

Every run spends provider minutes on the organization's own key, plus WayAI operations
(one per call turn, plus the call meter's minutes), within the cap below.
Ctrl-C hangs up the call in progress and closes its eval conversation, and no further run
starts; press it again to leave at once.

Options:
  --journey <name|id>       The journey to call (its responder must be a pilot_voice agent)
  --runs <n>                Runs to place, one after another (default ${DEFAULT_RUNS})
  --plan <file.json>        Per-line directives: interrupt, expect_delegation, readback, clip
  --clips <dir>             Pre-recorded caller audio: <dir>/<line>.wav (16-bit PCM)
  --tts-voice <voice>       Without clips, OPENAI_API_KEY synthesizes each line once, cached
                            (default ${DEFAULT_TTS_VOICE}; --tts-model, default ${DEFAULT_TTS_MODEL})
  --max-call-seconds <s>    Cap: a run's call length, enforced by the server (default ${DEFAULT_COST_CAP.maxCallSeconds})
  --max-operations <n>      Cap: a run's WayAI operations; the runner hangs up at it (default ${DEFAULT_COST_CAP.maxOperations})
  --usd-per-minute <x>      Your provider's price per call minute (default ${DEFAULT_COST_CAP.providerUsdPerMinute})
  --max-usd <x>             Suite budget of provider time; no run starts past it (default ${DEFAULT_COST_CAP.maxSuiteUsd})
  --latency-p95-ms <ms>     A run's latency passes at or under this p95 (default ${DEFAULT_THRESHOLDS.latencyP95MaxMs})
  --json <file>             Write the full report (every run's record and score)
  --hub <uuid|name>         Target hub when the workspace has more than one

Needs @roamhq/wrtc (WebRTC for Node), installed once next to the CLI:
  ${WRTC_INSTALL}
`.trim());
}
